// ERC-1155 Multi-Asset Platform - Styled Components
// 多元化RWA数字资产交易平台的专属样式组件
// 体现多样性、高效率、现代感和综合性

import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles'; 
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';

export const Accordion = styled(MuiAccordion)(({ theme }) => ({
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 255, 248, 0.98) 100%)'
    : 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 42, 23, 0.98) 100%)',
  backdropFilter: 'blur(16px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.spacing(2),
  boxShadow: theme.palette.mode === 'light'
    ? '0px 3px 12px rgba(34, 197, 94, 0.08), 0px 1px 6px rgba(59, 130, 246, 0.05)'
    : '0px 3px 12px rgba(34, 197, 94, 0.12), 0px 1px 6px rgba(59, 130, 246, 0.08)',
  margin: theme.spacing(1, 0),
  transition: theme.transitions.create([
    'box-shadow', 'border-color', 'background', 'transform'
  ], {
    duration: theme.transitions.duration.standard,
  }),
  
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: alpha(theme.palette.primary.main, 0.15),
    background: theme.palette.mode === 'light'
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(240, 255, 248, 1) 100%)'
      : 'linear-gradient(135deg, rgba(30, 41, 59, 1) 0%, rgba(15, 42, 23, 1) 100%)',
    boxShadow: theme.palette.mode === 'light'
      ? '0px 6px 20px rgba(34, 197, 94, 0.12), 0px 3px 10px rgba(59, 130, 246, 0.08)'
      : '0px 6px 20px rgba(34, 197, 94, 0.18), 0px 3px 10px rgba(59, 130, 246, 0.12)',
  },
  
  '&:before': {
    display: 'none',
  },
  
  '&.Mui-expanded': {
    margin: theme.spacing(1, 0),
    borderColor: alpha(theme.palette.primary.main, 0.2),
    background: theme.palette.mode === 'light'
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(240, 255, 248, 1) 100%)'
      : 'linear-gradient(135deg, rgba(30, 41, 59, 1) 0%, rgba(15, 42, 23, 1) 100%)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.info.main}, ${theme.palette.warning.main})`,
      borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
    },
  },
}));

// ERC-1155 Multi-Asset AccordionSummary Styles
export const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(30, 41, 59, 0.9)',
  flexDirection: 'row-reverse',
  padding: theme.spacing(2, 3),
  minHeight: 64,
  borderRadius: theme.spacing(2),
  transition: theme.transitions.create([
    'background-color', 'box-shadow', 'transform'
  ], {
    duration: theme.transitions.duration.short,
  }),
  
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.primary.main,
    transition: theme.transitions.create(['transform', 'color'], {
      duration: theme.transitions.duration.short,
    }),
    
    '&.Mui-expanded': {
      transform: 'rotate(180deg)',
      color: theme.palette.success.main,
    },
  },
  
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    
    '&.Mui-expanded': {
      margin: `${theme.spacing(1.5)} 0 ${theme.spacing(1.5)} ${theme.spacing(1)}`,
    },
    
    '&::before': {
      content: '""',
      width: '4px',
      height: '20px',
      background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
      borderRadius: '2px',
      marginRight: theme.spacing(1),
    },
  },
  
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 1)'
      : 'rgba(30, 41, 59, 1)',
    boxShadow: theme.palette.mode === 'light'
      ? '0px 2px 8px rgba(34, 197, 94, 0.08)'
      : '0px 2px 8px rgba(34, 197, 94, 0.12)',
    transform: 'translateX(2px)',
  },
}));

// ERC-1155 Multi-Asset Card Style - 多元化卡片样式
export const CardStyle = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(3),
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 255, 248, 0.98) 100%)'
    : 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 42, 23, 0.98) 100%)',
  backdropFilter: 'blur(16px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: theme.palette.mode === 'light'
    ? '0px 6px 24px rgba(34, 197, 94, 0.12), 0px 3px 12px rgba(59, 130, 246, 0.08)'
    : '0px 6px 24px rgba(34, 197, 94, 0.2), 0px 3px 12px rgba(59, 130, 246, 0.15)',
  transition: theme.transitions.create([
    'transform', 'box-shadow', 'border-color', 'background'
  ], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  
  // '&:hover': {
  //   transform: 'translateY(-1px) scale(1.01)',
  //   borderColor: alpha(theme.palette.primary.main, 0.2),
  //   background: theme.palette.mode === 'light'
  //     ? 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(240, 255, 248, 1) 100%)'
  //     : 'linear-gradient(135deg, rgba(30, 41, 59, 1) 0%, rgba(15, 42, 23, 1) 100%)',
  //   boxShadow: theme.palette.mode === 'light'
  //     ? '0px 12px 36px rgba(34, 197, 94, 0.15), 0px 6px 18px rgba(59, 130, 246, 0.12)'
  //     : '0px 12px 36px rgba(34, 197, 94, 0.25), 0px 6px 18px rgba(59, 130, 246, 0.2)',
  // },
  
  // Multi-asset gradient border
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.info.main}, ${theme.palette.warning.main}, ${theme.palette.secondary.main})`,
    borderRadius: `${theme.spacing(2.5)} ${theme.spacing(2.5)} 0 0`,
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
    }),
  },
  
  '&:hover::before': {
    opacity: 1,
  },
  
  // Multi-asset indicator
  '&::after': {
    content: '""',
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
    opacity: 0.8,
    animation: 'multi-asset-pulse 2s infinite',
  },
  
  '@keyframes multi-asset-pulse': {
    '0%, 100%': {
      opacity: 0.8,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.1)',
    },
  },
}));
// ERC-1155 Multi-Asset Gradient Style - 多元化渐变样式
export const GradientStyle = styled('div')(({ theme, sx }) => ({
  background: theme.palette.mode === 'light'
    ? `linear-gradient(135deg, ${alpha('#22C55E', 0.08)} 0%, ${alpha('#3B82F6', 0.06)} 25%, ${alpha('#F97316', 0.05)} 50%, ${alpha('#A855F7', 0.07)} 75%, ${alpha('#22C55E', 0.06)} 100%)`
    : `linear-gradient(135deg, ${alpha('#22C55E', 0.12)} 0%, ${alpha('#3B82F6', 0.1)} 25%, ${alpha('#F97316', 0.08)} 50%, ${alpha('#A855F7', 0.11)} 75%, ${alpha('#22C55E', 0.09)} 100%)`,
  borderRadius: '10px',
  padding: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  transition: theme.transitions.create(['background', 'transform'], {
    duration: theme.transitions.duration.standard,
  }),
  
  // '&:hover': {
  //   transform: 'scale(1.01)',
  //   background: theme.palette.mode === 'light'
  //     ? `linear-gradient(135deg, ${alpha('#22C55E', 0.12)} 0%, ${alpha('#3B82F6', 0.1)} 25%, ${alpha('#F97316', 0.08)} 50%, ${alpha('#A855F7', 0.11)} 75%, ${alpha('#22C55E', 0.1)} 100%)`
  //     : `linear-gradient(135deg, ${alpha('#22C55E', 0.16)} 0%, ${alpha('#3B82F6', 0.14)} 25%, ${alpha('#F97316', 0.12)} 50%, ${alpha('#A855F7', 0.15)} 75%, ${alpha('#22C55E', 0.13)} 100%)`,
  // },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'light'
      ? `radial-gradient(circle at 20% 30%, ${alpha('#22C55E', 0.12)} 0%, transparent 40%),
         radial-gradient(circle at 80% 20%, ${alpha('#3B82F6', 0.1)} 0%, transparent 40%),
         radial-gradient(circle at 60% 80%, ${alpha('#F97316', 0.08)} 0%, transparent 40%),
         radial-gradient(circle at 30% 70%, ${alpha('#A855F7', 0.09)} 0%, transparent 40%)`
      : `radial-gradient(circle at 20% 30%, ${alpha('#22C55E', 0.16)} 0%, transparent 40%),
         radial-gradient(circle at 80% 20%, ${alpha('#3B82F6', 0.14)} 0%, transparent 40%),
         radial-gradient(circle at 60% 80%, ${alpha('#F97316', 0.12)} 0%, transparent 40%),
         radial-gradient(circle at 30% 70%, ${alpha('#A855F7', 0.13)} 0%, transparent 40%)`,
    pointerEvents: 'none',
    animation: 'multi-gradient-flow 8s ease-in-out infinite',
  },
  
  '@keyframes multi-gradient-flow': {
    '0%, 100%': {
      opacity: 1,
      transform: 'scale(1) rotate(0deg)',
    },
    '50%': {
      opacity: 0.8,
      transform: 'scale(1.05) rotate(2deg)',
    },
  },
  
  ...sx,
}));
  

export const IconifyLink = styled('a')(({ theme }) => (
    {
        textDecoration: 'none',
        cursor: "pointer",
        color: theme.palette.primary.main,
    }
));

export const BlankLink = styled('a')(({ theme, selected }) => (
    {
        textDecoration: selected ? 'underline' : 'none',
        cursor: "pointer",
        color: selected ? theme.palette.primary.main : theme.palette.text.primary,
    }
));

export const BackgroundImageStyle = styled('div')(({ theme, sx }) => (
    {
        ...sx,
        display: 'flex',
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
    }
));

// ERC-1155 Multi-Asset Toggle Button - 多元化切换按钮
export const ToggleButton = styled('div')(({ theme, selected }) => ({
  padding: '16px 0',
  textAlign: 'center',
  borderRadius: '14px',
  width: '100%',
  background: selected 
    ? `linear-gradient(135deg, #22C55E 0%, #3B82F6 25%, #F97316 50%, #A855F7 75%, #22C55E 100%)`
    : theme.palette.mode === 'light' 
      ? `linear-gradient(145deg, #ffffff 0%, ${alpha('#F0FDF4', 0.95)} 100%)` 
      : `linear-gradient(145deg, ${theme.palette.grey[800]} 0%, ${alpha('#0F2A17', 0.95)} 100%)`,
  cursor: 'pointer',
  color: selected 
    ? 'white' 
    : theme.palette.text.primary,
  boxShadow: selected
    ? `0px 8px 24px ${alpha('#22C55E', 0.25)}, 0px 4px 12px ${alpha('#3B82F6', 0.15)}`
    : `0px 3px 10px ${alpha(theme.palette.grey[500], 0.08)}`,
  border: selected
    ? 'none'
    : `1px solid ${theme.palette.mode === 'light' ? alpha('#22C55E', 0.15) : alpha('#3B82F6', 0.2)}`,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fontWeight: selected ? 600 : 500,
  letterSpacing: selected ? '0.3px' : '0.1px',
  
  '&::before': selected ? {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${alpha('#ffffff', 0.15)}, transparent)`,
    animation: 'multi-shimmer 2.5s infinite',
  } : {},
  
  '&::after': selected ? {
    content: '""',
    position: 'absolute',
    top: '2px',
    left: '2px',
    right: '2px',
    height: '2px',
    background: `linear-gradient(90deg, ${alpha('#ffffff', 0.6)}, ${alpha('#ffffff', 0.3)}, ${alpha('#ffffff', 0.6)})`,
    borderRadius: '12px 12px 0 0',
  } : {},
  
  '&:hover': {
    transform: 'translateY(-2px) scale(1.01)',
    boxShadow: selected
      ? `0px 12px 32px ${alpha('#22C55E', 0.3)}, 0px 6px 16px ${alpha('#3B82F6', 0.2)}`
      : `0px 6px 16px ${alpha(theme.palette.grey[500], 0.12)}`,
  },
  
  '@keyframes multi-shimmer': {
    '0%': {
      left: '-100%',
    },
    '100%': {
      left: '100%',
    },
  },
  '@keyframes shimmer': {
    '0%': { left: '-100%' },
    '100%': { left: '100%' },
  },
}));

// ERC-1155 Multi-Asset Select Style - 多元化选择框样式
export const SelectStyle = styled('select')(({ theme, sx }) => ({
  outline: 'none',
  border: theme.palette.mode === 'light'
    ? `2px solid ${alpha('#22C55E', 0.2)}`
    : `2px solid ${alpha('#3B82F6', 0.3)}`,
  borderRadius: '14px',
  padding: '16px 18px',
  fontSize: '15px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  background: theme.palette.mode === 'light'
    ? `linear-gradient(145deg, #ffffff 0%, ${alpha('#F0FDF4', 0.95)} 100%)`
    : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha('#0F2A17', 0.95)} 100%)`,
  boxShadow: theme.palette.mode === 'light'
    ? `0px 3px 10px ${alpha('#22C55E', 0.08)}`
    : `0px 3px 10px ${alpha('#3B82F6', 0.12)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  letterSpacing: '0.1px',
  position: 'relative',
  
  '&:focus': {
    borderColor: theme.palette.mode === 'light' ? '#22C55E' : '#3B82F6',
    boxShadow: theme.palette.mode === 'light'
      ? `0px 6px 16px ${alpha('#22C55E', 0.15)}`
      : `0px 6px 16px ${alpha('#3B82F6', 0.2)}`,
    transform: 'translateY(-1px)',
  },
  
  '&:hover': {
    borderColor: theme.palette.mode === 'light'
      ? alpha('#22C55E', 0.4)
      : alpha('#3B82F6', 0.5),
    transform: 'translateY(-1px)',
    background: theme.palette.mode === 'light'
      ? `linear-gradient(145deg, #ffffff 0%, ${alpha('#F0FDF4', 1)} 100%)`
      : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha('#0F2A17', 1)} 100%)`,
  },
  
  '& option': {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: '10px',
    borderRadius: '8px',
  },
  
  ...sx,
}));

// ERC-1155 Multi-Asset Search Bar - 多元化搜索栏样式
export const SearchBar = styled('div')(({ theme, sx }) => ({
  borderRadius: 18,
  display: 'flex',
  justifyContent: 'space-between',
  overflow: 'hidden',
  background: theme.palette.mode === 'light'
    ? `linear-gradient(145deg, #ffffff 0%, ${alpha('#F0FDF4', 0.98)} 100%)`
    : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha('#0F2A17', 0.98)} 100%)`,
  boxShadow: theme.palette.mode === 'light'
    ? `0px 6px 20px ${alpha('#22C55E', 0.1)}, 0px 3px 10px ${alpha('#3B82F6', 0.05)}`
    : `0px 6px 20px ${alpha('#22C55E', 0.15)}, 0px 3px 10px ${alpha('#3B82F6', 0.1)}`,
  border: theme.palette.mode === 'light'
    ? `2px solid ${alpha('#22C55E', 0.2)}`
    : `2px solid ${alpha('#3B82F6', 0.3)}`,
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'light'
      ? `linear-gradient(135deg, ${alpha('#22C55E', 0.02)} 0%, ${alpha('#3B82F6', 0.01)} 100%)`
      : `linear-gradient(135deg, ${alpha('#22C55E', 0.03)} 0%, ${alpha('#3B82F6', 0.02)} 100%)`,
    pointerEvents: 'none',
  },
  
  '&:focus-within': {
    transform: 'translateY(-1px)',
    boxShadow: theme.palette.mode === 'light'
      ? `0px 8px 24px ${alpha('#22C55E', 0.15)}, 0px 4px 12px ${alpha('#3B82F6', 0.08)}`
      : `0px 8px 24px ${alpha('#22C55E', 0.2)}, 0px 4px 12px ${alpha('#3B82F6', 0.12)}`,
    borderColor: theme.palette.mode === 'light' ? '#22C55E' : '#3B82F6',
  },
  '& input': {
    border: 'none',
    width: '100%',
    padding: '18px 22px',
    fontSize: '15px',
    fontWeight: 500,
    background: 'transparent',
    color: theme.palette.text.primary,
    letterSpacing: '0.1px',
    
    '&:focus, &:active': {
      outline: 'none',
    },
    
    '&::placeholder': {
      color: theme.palette.text.secondary,
      fontWeight: 400,
    },
  },
  
  '& button': {
    background: `linear-gradient(135deg, #22C55E 0%, #3B82F6 25%, #F97316 50%, #A855F7 75%, #22C55E 100%)`,
    border: 'none',
    padding: '18px 26px',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 600,
    letterSpacing: '0.3px',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: `linear-gradient(90deg, transparent, ${alpha('#ffffff', 0.2)}, transparent)`,
      transition: 'left 0.5s ease',
    },
    
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: `0px 6px 16px ${alpha('#22C55E', 0.25)}, 0px 3px 8px ${alpha('#3B82F6', 0.15)}`,
      
      '&::before': {
        left: '100%',
      },
    },
    
    '&:active': {
      transform: 'scale(0.99)',
    },
  },
  ...sx,
}));

// ERC-1155 Multi-Asset Root Container - 多元化根容器样式
export const RootStyle = styled('div')(({ theme, sx }) => ({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
  background: theme.palette.mode === 'light'
    ? `linear-gradient(135deg, #FAFAFA 0%, ${alpha('#F0FDF4', 0.8)} 30%, ${alpha('#EFF6FF', 0.6)} 70%, #FFFFFF 100%)`
    : `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha('#0F2A17', 0.9)} 30%, ${alpha('#1E3A8A', 0.7)} 70%, ${theme.palette.background.paper} 100%)`,
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'light'
      ? `radial-gradient(circle at 25% 75%, ${alpha('#22C55E', 0.02)} 0%, transparent 40%), radial-gradient(circle at 75% 25%, ${alpha('#3B82F6', 0.015)} 0%, transparent 40%), radial-gradient(circle at 50% 50%, ${alpha('#F97316', 0.01)} 0%, transparent 30%)`
      : `radial-gradient(circle at 25% 75%, ${alpha('#22C55E', 0.04)} 0%, transparent 40%), radial-gradient(circle at 75% 25%, ${alpha('#3B82F6', 0.03)} 0%, transparent 40%), radial-gradient(circle at 50% 50%, ${alpha('#A855F7', 0.02)} 0%, transparent 30%)`,
    pointerEvents: 'none',
  },
  ...sx,
}));

// ERC-1155 Multi-Asset Table - 多元化表格样式
export const TableStyle = styled('table')(({ theme, sx }) => ({
  width: '100%',
  color: theme.palette.text.primary,
  borderRadius: 16,
  overflow: 'hidden',
  background: theme.palette.mode === 'light'
    ? `linear-gradient(145deg, #ffffff 0%, ${alpha('#F0FDF4', 0.95)} 100%)`
    : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha('#0F2A17', 0.95)} 100%)`,
  boxShadow: theme.palette.mode === 'light'
    ? `0px 12px 40px ${alpha('#22C55E', 0.12)}, 0px 8px 24px ${alpha('#3B82F6', 0.06)}`
    : `0px 12px 40px ${alpha('#22C55E', 0.18)}, 0px 8px 24px ${alpha('#3B82F6', 0.12)}`,
  border: theme.palette.mode === 'light'
    ? `1px solid ${alpha('#22C55E', 0.15)}`
    : `1px solid ${alpha('#3B82F6', 0.25)}`,
  '& thead tr th': {
    borderTop: 'none',
    borderBottom: theme.palette.mode === 'light'
      ? `2px solid ${alpha('#22C55E', 0.2)}`
      : `2px solid ${alpha('#3B82F6', 0.3)}`,
    background: `linear-gradient(135deg, #22C55E 0%, #3B82F6 25%, #F97316 50%, #A855F7 75%, #22C55E 100%)`,
    fontWeight: 600,
    fontSize: 14,
    color: 'white',
    verticalAlign: 'bottom',
    padding: '18px 20px',
    textAlign: 'left',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: `linear-gradient(90deg, ${alpha('#ffffff', 0.4)} 0%, ${alpha('#ffffff', 0.2)} 50%, ${alpha('#ffffff', 0.4)} 100%)`,
    },
  },
  '& tbody tr': {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? alpha('#22C55E', 0.04)
        : alpha('#3B82F6', 0.06),
      color: theme.palette.mode === 'light' ? '#22C55E' : '#3B82F6',
      transform: 'scale(1.01)',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        background: theme.palette.mode === 'light'
          ? `linear-gradient(180deg, #22C55E 0%, #3B82F6 50%, #F97316 100%)`
          : `linear-gradient(180deg, #3B82F6 0%, #22C55E 50%, #A855F7 100%)`,
      },
    },
  },
  '& tbody tr td': {
    background: 'transparent',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.palette.mode === 'light' ? alpha('#22C55E', 0.08) : alpha('#3B82F6', 0.12)}`,
    verticalAlign: 'middle',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    transition: 'all 0.2s ease',
  },
  '& tbody tr td img': {
    height: 48,
    borderRadius: 12,
    verticalAlign: 'middle',
    border: theme.palette.mode === 'light'
      ? `2px solid ${alpha('#22C55E', 0.15)}`
      : `2px solid ${alpha('#3B82F6', 0.25)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1)',
      borderColor: theme.palette.mode === 'light' ? '#22C55E' : '#3B82F6',
    },
  },
  ...sx,
}));
// ERC-1155 Normal Table - 多元化普通表格样式
export const NormalTableStyle = styled('table')(({ theme, sx }) => ({
  width: '100%',
  borderRadius: 14,
  overflow: 'hidden',
  background: theme.palette.mode === 'light'
    ? alpha('#F0FDF4', 0.95)
    : alpha('#0F2A17', 0.95),
  boxShadow: theme.palette.mode === 'light'
    ? `0px 6px 20px ${alpha('#22C55E', 0.08)}, 0px 3px 10px ${alpha('#3B82F6', 0.04)}`
    : `0px 6px 20px ${alpha('#22C55E', 0.12)}, 0px 3px 10px ${alpha('#3B82F6', 0.08)}`,
  border: theme.palette.mode === 'light'
    ? `1px solid ${alpha('#22C55E', 0.12)}`
    : `1px solid ${alpha('#3B82F6', 0.2)}`,
  
  '& thead tr th': {
    borderTop: 'none',
    borderBottom: theme.palette.mode === 'light'
      ? `2px solid ${alpha('#22C55E', 0.15)}`
      : `2px solid ${alpha('#3B82F6', 0.2)}`,
    borderRight: theme.palette.mode === 'light'
      ? `1px solid ${alpha('#22C55E', 0.08)}`
      : `1px solid ${alpha('#3B82F6', 0.12)}`,
    background: theme.palette.mode === 'light'
      ? `linear-gradient(135deg, ${alpha('#22C55E', 0.1)} 0%, ${alpha('#3B82F6', 0.06)} 100%)`
      : `linear-gradient(135deg, ${alpha('#22C55E', 0.15)} 0%, ${alpha('#3B82F6', 0.1)} 100%)`,
    fontWeight: 600,
    fontSize: 14,
    verticalAlign: 'bottom',
    padding: '14px 18px',
    cursor: 'pointer',
    color: theme.palette.text.primary,
    transition: 'all 0.3s ease',
    letterSpacing: '0.3px',
    
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? alpha('#22C55E', 0.08)
        : alpha('#3B82F6', 0.12),
    },
  },
  
  '& tbody tr': {
    transition: 'all 0.3s ease',
    position: 'relative',
    
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? alpha('#22C55E', 0.03)
        : alpha('#3B82F6', 0.05),
      transform: 'translateX(2px)',
    },
  },
  
  '& tbody tr td': {
    padding: '12px 18px',
    borderBottom: theme.palette.mode === 'light'
      ? `1px solid ${alpha('#22C55E', 0.06)}`
      : `1px solid ${alpha('#3B82F6', 0.1)}`,
    borderRight: theme.palette.mode === 'light'
      ? `1px solid ${alpha('#22C55E', 0.04)}`
      : `1px solid ${alpha('#3B82F6', 0.08)}`,
    verticalAlign: 'middle',
    fontSize: 13,
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease',
  },
  
  '& tbody tr td:first-of-type, & thead tr th:first-of-type': {
    borderLeft: 'none',
  },
  
  '& tbody tr td img': {
    height: 42,
    borderRadius: 10,
    verticalAlign: 'middle',
    border: theme.palette.mode === 'light'
      ? `1px solid ${alpha('#22C55E', 0.1)}`
      : `1px solid ${alpha('#3B82F6', 0.15)}`,
    transition: 'all 0.3s ease',
    
    '&:hover': {
      transform: 'scale(1.05)',
      borderColor: theme.palette.mode === 'light' ? '#22C55E' : '#3B82F6',
    },
  },
  ...sx,
}));
